type Primitive = string | number | boolean | null | undefined;
type JSONValue = Primitive | JSONObject | JSONValue[];

export interface JSONObject {
  [key: string]: JSONValue;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function cleanFalsyValues<T extends JSONValue>(
  value: T,
  skipKeys?: string[]
): T | undefined {
  if (Array.isArray(value)) {
    const cleaned = value
      .map((v) => cleanFalsyValues(v, skipKeys))
      .filter(
        (v): v is JSONValue =>
          v !== undefined &&
          !(
            typeof v === 'object' &&
            v !== null &&
            !Array.isArray(v) &&
            Object.keys(v).length === 0
          ) &&
          !(Array.isArray(v) && v.length === 0)
      );

    return (cleaned.length ? cleaned : undefined) as T | undefined;
  }

  if (isObject(value)) {
    const entries = Object.entries(value)
      .map(([key, val]) => {
        if (skipKeys && skipKeys.includes(key)) {
          return [key, val] as const;
        }

        return [key, cleanFalsyValues(val as JSONValue, skipKeys)] as const;
      })
      .filter((entry): entry is [string, Exclude<JSONValue, undefined>] => {
        const v = entry[1];
        return (
          v !== undefined &&
          !(isObject(v) && Object.keys(v).length === 0) &&
          !(Array.isArray(v) && v.length === 0)
        );
      });

    return entries.length ? (Object.fromEntries(entries) as T) : undefined;
  }

  return value ? value : undefined;
}
